/**
 * On-device storage for the trial mode. Not the product's data layer.
 *
 * `CLAUDE.md` is explicit that Fortunaut is not local-first: a balance is money
 * a parent owes, so a client that can write the ledger can mint dollars, and
 * accrual needs a clock a child can't move by changing the tablet's date. All
 * of that still stands. This file exists so one adult can carry the thing
 * around for a week and find out whether the idea is any good, on a phone,
 * without a backend — and it is deliberately walled off in its own route so
 * nothing in the real path can start depending on it.
 *
 * What it does share with the real path is the domain module, unchanged. The
 * arithmetic being exercised here is the arithmetic that ships.
 */

import { dueEnvelopeEntries } from '../domain/envelope.js';
import { toDateOnly } from '../domain/periods.js';

const KEY = 'fortunaut.trial.v1';

export const FAMILY_ID = 'trial-family';
export const ENVELOPE_ID = 'trial-envelope';

export function read() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    // A corrupt or unreadable store is a fresh start, not a white screen.
    return null;
  }
}

export function write(state) {
  localStorage.setItem(KEY, JSON.stringify(state));
  return state;
}

export function clear() {
  localStorage.removeItem(KEY);
}

export function create({ rule, tiles, goal, startedOn }) {
  return write({
    rule,
    tiles: tiles.map((t, i) => ({ ...t, id: `tile-${i}` })),
    goal: goal ? { ...goal, id: 'goal-1' } : null,
    startedOn,
    entries: [],
    proposals: [],
  });
}

/**
 * Bring the envelope up to date, then hand back the state.
 *
 * This is the client standing in for the server-side job, and it works
 * unchanged because `dueEnvelopeEntries` is keyed on the period index rather
 * than on when it happened to run. Opening the app after ten days away inserts
 * the refills that were missed *and* rolls each closed period's leftover into
 * the goal, in order — the same history the job would have written, and
 * running it twice adds nothing.
 */
export function catchUp(state, today = toDateOnly(new Date())) {
  if (!state?.rule) return state;
  const due = dueEnvelopeEntries({
    familyId: FAMILY_ID,
    envelopeId: ENVELOPE_ID,
    goalId: state.goal?.id ?? null,
    goalFromDate: state.goal?.startedOn ?? null,
    rule: state.rule,
    existing: state.entries,
    fromDate: state.startedOn,
    toDate: today,
  });
  if (!due.length) return state;
  return write({ ...state, entries: [...state.entries, ...due] });
}

/** Proposals are mutable state, not ledger entries — they resolve to one. */
export function putProposal(state, proposal) {
  const proposals = state.proposals ?? [];
  const next = proposals.some((p) => p.id === proposal.id)
    ? proposals.map((p) => (p.id === proposal.id ? proposal : p))
    : [...proposals, proposal];
  return write({ ...state, proposals: next });
}

/** Append-only here too. First write wins, same as the real ledger. */
export function append(state, entry) {
  if (state.entries.some((e) => e.id === entry.id)) return state;
  return write({ ...state, entries: [...state.entries, entry] });
}
